import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Instagram } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import PixoHero from "@/components/pixo/PixoHero";
import { PixelNumber } from "@/components/pixo/eco";

function useCountdown(iso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!iso) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [iso]);
  if (!iso) return null;
  const ms = new Date(iso).getTime() - now;
  if (Number.isNaN(ms) || ms <= 0) return null;
  const s = Math.floor(ms / 1000);
  return { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
}

/** Public page while the site is in pre-launch mode. */
export default function ComingSoon({ launchAt, instagram }) {
  const { t, lang, setLang } = useI18n();
  const C = t.soon;
  const { login, loading } = useAuth();
  const cd = useCountdown(launchAt);
  const [open, setOpen] = useState(false);
  const [ident, setIdent] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setErr("");
    try {
      await login(ident, pw);
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setErr(d === "prelaunch" ? C.no_access : ex?.response?.status === 429 ? C.too_many : C.bad_login);
    }
  }

  const date = launchAt
    ? new Date(launchAt).toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB", { day: "numeric", month: "long" })
    : null;

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-stone-100 flex flex-col soon-bg" data-testid="coming-soon">
      <Helmet>
        <title>{`tierTCG · ${C.title_tag}`}</title>
      </Helmet>
      <header className="flex items-center justify-between px-5 sm:px-10 pt-6">
        <img src="/brand/tiertcg-logo.webp" alt="tierTCG" className="h-7 sm:h-8 w-auto" />
        <div className="flex border border-amber-500/25 rounded-sm overflow-hidden text-[10px] font-bold tracking-[0.18em]">
          {["pt", "en"].map((l) => (
            <button key={l} type="button" onClick={() => setLang(l)} className={`px-2.5 py-1.5 uppercase ${lang === l ? "bg-amber-500/15 text-amber-300" : "text-stone-500 hover:text-stone-300"}`}>{l}</button>
          ))}
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center text-center px-5 py-10">
        <PixoHero scale={5} lines={C.pixie_lines} />
        <div className="mt-6 text-[11px] uppercase tracking-[0.3em] font-bold text-amber-400/90">{C.eyebrow}</div>
        <h1 className="font-display font-black uppercase leading-[0.92] tracking-tight text-[40px] sm:text-[64px] mt-3 max-w-3xl text-balance">
          {C.h1a} <span className="text-amber-400">{C.h1b}</span> {C.h1c}
        </h1>
        <p className="text-stone-400 mt-4 max-w-md text-[15px]">{C.text}</p>

        {cd ? (
          <div className="mt-8 flex items-end gap-4 sm:gap-6" aria-label={date || ""} data-testid="soon-countdown">
            {[["d", C.days], ["h", C.hours], ["m", C.mins], ["s", C.secs]].map(([k, label]) => (
              <div key={k} className="flex flex-col items-center gap-2">
                <PixelNumber value={String(cd[k]).padStart(2, "0")} px={5} color="#F5C518" />
                <span className="text-[10px] uppercase tracking-[0.22em] text-stone-500 font-bold">{label}</span>
              </div>
            ))}
          </div>
        ) : null}
        {date && <div className="mt-4 text-xs text-stone-500">{C.opens_on.replace("{d}", date)}</div>}

        {instagram && (
          <a href={instagram} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center gap-2 border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 px-5 py-3 text-[11px] font-bold uppercase tracking-[0.18em] rounded-sm">
            <Instagram className="w-4 h-4" /> {C.follow}
          </a>
        )}
      </main>

      <footer className="px-5 pb-6 flex flex-col items-center gap-3 text-xs text-stone-600">
        {!open ? (
          <button type="button" onClick={() => setOpen(true)} className="text-stone-500 hover:text-amber-300 underline underline-offset-4 decoration-stone-700" data-testid="soon-login-open">
            {C.tester_link}
          </button>
        ) : (
          <form onSubmit={submit} className="w-full max-w-xs flex flex-col gap-2 border border-amber-500/20 bg-[#121216] p-4 rounded-sm text-left soon-pop" data-testid="soon-login">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-400/90">{C.tester_title}</div>
            <input id="soon-user" value={ident} onChange={(e) => setIdent(e.target.value)} autoComplete="username" placeholder={C.user} className="bg-[#0A0A0C] border border-white/10 rounded-sm px-3 py-2 text-[16px] text-stone-100 focus:border-amber-400/60 outline-none" />
            <input id="soon-pass" type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="current-password" placeholder={C.pass} className="bg-[#0A0A0C] border border-white/10 rounded-sm px-3 py-2 text-[16px] text-stone-100 focus:border-amber-400/60 outline-none" />
            {err && <div className="text-[12px] text-red-400">{err}</div>}
            <button type="submit" disabled={loading || !ident || !pw} className="btn-gold disabled:opacity-50 py-2.5 text-[11px] font-bold uppercase tracking-[0.18em] rounded-sm">{C.enter}</button>
          </form>
        )}
        <div>© 2026 tierTCG · {C.by}</div>
      </footer>
    </div>
  );
}
